import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  subscribeToNotifications, 
  isSubscribedToNotifications,
  unsubscribeFromNotifications 
} from '../services/firebaseService';

// Types for separate button states
type PWAButtonState = 'hidden' | 'install' | 'open-app' | 'processing';
type NotificationButtonState = 'hidden' | 'subscribe' | 'subscribed' | 'processing';

// Base button styling (shared between PWA and Notification)
const BaseButton = styled.button<{ color: string }>`
  position: fixed;
  right: 16px;
  background: none;
  border: none;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
  z-index: 3;
  padding: 6px;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// PWA Button (moved below speed button)
const PWAButton = styled(BaseButton)`
  top: calc(50% + 210px); /* Below notification button */
  position: absolute;
`;

// Notification Button (above PWA button)
const NotificationButton = styled(BaseButton)`
  top: calc(50% + 150px); /* Above PWA button */
  position: absolute;
`;

const ToastNotification = styled.div<{ visible: boolean; type: 'success' | 'info' | 'error' | 'warning' }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ type }) => 
    type === 'success' ? 'rgba(46, 204, 113, 0.95)' :
    type === 'error' ? 'rgba(231, 76, 60, 0.95)' :
    type === 'warning' ? 'rgba(243, 156, 18, 0.95)' :
    'rgba(52, 152, 219, 0.95)'
  };
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 999999;
  font-size: 14px;
  font-weight: 500;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: ${({ visible }) => 
    visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-20px)'
  };
  transition: all 0.3s ease;
  pointer-events: none;
  max-width: 90vw;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
`;

// PWA Detection utilities
const isPWASupported = (): boolean => {
  return 'serviceWorker' in navigator && 'beforeinstallprompt' in window;
};

const isPWAInstalled = (): boolean => {
  // Multiple detection methods
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOS = (window.navigator as any).standalone === true;
  const isAndroidApp = document.referrer.includes('android-app://');
  
  return isStandalone || isIOS || isAndroidApp;
};

const isInPWA = (): boolean => {
  return isPWAInstalled();
};

const isNotificationSupported = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Get PWA button state
const getPWAButtonState = (): PWAButtonState => {
  if (!isPWASupported()) return 'hidden';
  if (isInPWA()) return 'hidden'; // Hide when in PWA
  if (!isPWAInstalled()) return 'install';
  return 'open-app';
};

// Get notification button state
const getNotificationButtonState = (): NotificationButtonState => {
  if (!isNotificationSupported()) return 'hidden';
  if (isInPWA()) return 'hidden'; // Hide when in PWA
  if (isSubscribedToNotifications()) return 'hidden'; // Hide when subscribed
  return 'subscribe';
};

// Global PWA install prompt
let deferredPrompt: any = null;

// Props interface
interface SimpleNotificationButtonProps {
  visible: boolean;
  onSubscriptionChange?: (subscribed: boolean) => void;
}

// Main component
const SimpleNotificationButton: React.FC<SimpleNotificationButtonProps> = ({ 
  visible, 
  onSubscriptionChange 
}) => {
  const [pwaState, setPwaState] = useState<PWAButtonState>('hidden');
  const [notificationState, setNotificationState] = useState<NotificationButtonState>('hidden');
  const [isProcessing, setIsProcessing] = useState(false);
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'info' | 'error' | 'warning';
  }>({
    visible: false,
    message: '',
    type: 'info'
  });

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'info' | 'error' | 'warning' = 'info') => {
    setToast({ visible: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 4000);
  }, []);

  // Update both button states
  const updateStates = useCallback(() => {
    const newPWAState = getPWAButtonState();
    const newNotificationState = getNotificationButtonState();
    
    setPwaState(newPWAState);
    setNotificationState(newNotificationState);
    
    console.log('🔄 Button states updated:', { 
      pwa: newPWAState, 
      notification: newNotificationState,
      isInstalled: isPWAInstalled(),
      isInPWA: isInPWA(),
      isSubscribed: isSubscribedToNotifications()
    });
    
    return { pwa: newPWAState, notification: newNotificationState };
  }, []);

  // Listen for PWA install events
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e;
      console.log('📱 PWA install prompt available');
      updateStates();
    };

    const handleAppInstalled = () => {
      deferredPrompt = null;
      console.log('✅ PWA installed successfully');
      showToast('🎉 App đã được cài đặt thành công!', 'success');
      // Force state update after installation
      setTimeout(() => {
        updateStates();
      }, 1000);
    };

    // Listen for display mode changes (PWA launch)
    const handleDisplayModeChange = () => {
      console.log('🔄 Display mode changed');
      setTimeout(() => {
        updateStates();
      }, 500);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Listen for navigation changes that might indicate PWA state change
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addListener(handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeListener(handleDisplayModeChange);
    };
  }, [updateStates, showToast]);

  // Initialize and periodic state checks
  useEffect(() => {
    updateStates();
    
    // Periodic state check every 30 seconds to catch state changes (reduced frequency)
    const interval = setInterval(() => {
      updateStates();
    }, 30000);
    
    // Listen for localStorage changes that might affect subscription state
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'fcm_subscribed' || e.key === 'fcm_token') {
        console.log('🔄 Subscription storage changed, updating button state');
        // Debounce to prevent rapid state changes
        setTimeout(() => updateStates(), 500);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for focus/visibility changes to refresh state
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('📱 Page became visible, refreshing notification state');
        // Debounce visibility changes
        setTimeout(() => updateStates(), 1000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateStates]);

  // Handle PWA installation
  const handlePWAInstall = useCallback(async () => {
    if (!deferredPrompt) {
      showToast('⚠️ Không thể cài đặt app. Hãy thử refresh trang', 'warning');
      return;
    }

    try {
      setIsProcessing(true);
      setPwaState('processing');
      showToast('📱 Đang cài đặt ứng dụng...', 'info');

      const result = await deferredPrompt.prompt();
      
      if (result.outcome === 'accepted') {
        showToast('✅ Cài đặt thành công! Đợi một chút...', 'success');
        deferredPrompt = null;
        // The appinstalled event will handle state update
      } else {
        showToast('❌ Cài đặt app bị hủy', 'warning');
        setPwaState('install');
      }
    } catch (error) {
      console.error('PWA install error:', error);
      showToast('❌ Lỗi khi cài đặt app', 'error');
      setPwaState('install');
    } finally {
      setIsProcessing(false);
    }
  }, [showToast]);

  // Handle notification subscription
  const handleNotificationSubscribe = useCallback(async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setNotificationState('processing');
      showToast('🔔 Đang thiết lập thông báo...', 'info');

      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        showToast('❌ Cần cấp quyền thông báo để tiếp tục', 'warning');
        setNotificationState('subscribe');
        setIsProcessing(false);
        return;
      }

      const result = await subscribeToNotifications();
      
      if (result.success) {
        setNotificationState('hidden'); // Hide after successful subscription
        showToast('🎉 Đăng ký thông báo thành công!', 'success');
        onSubscriptionChange?.(true);
        
        // Send welcome notification
        setTimeout(async () => {
          try {
            await fetch('/api/notifications/send-welcome', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                token: result.token,
                timestamp: new Date().toISOString()
              }),
            });
          } catch (error) {
            console.error('Welcome notification error:', error);
          }
        }, 2000);
      } else {
        showToast(`❌ Đăng ký thất bại: ${result.error}`, 'error');
        setNotificationState('subscribe');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      showToast('❌ Có lỗi xảy ra khi đăng ký', 'error');
      setNotificationState('subscribe');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, showToast, onSubscriptionChange]);

  // Handle opening PWA
  const handleOpenPWA = useCallback(() => {
    if (isInPWA()) {
      showToast('📱 Bạn đã đang sử dụng app!', 'info');
      return;
    }

    // Try to open as PWA
    window.location.href = window.location.origin + '/?utm_source=pwa-button';
    showToast('📱 Mở ứng dụng...', 'info');
  }, [showToast]);

  // Loading spinner SVG
  const loadingSpinner = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
      <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          attributeType="XML"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="1s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );

  // PWA button content
  const getPWAButtonContent = () => {
    if (pwaState === 'processing') return loadingSpinner;
    
    if (pwaState === 'install') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7z" fill="currentColor"/>
          <rect x="5" y="18" width="14" height="2" fill="currentColor"/>
        </svg>
      );
    }
    
    if (pwaState === 'open-app') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7z" fill="currentColor"/>
          <path d="M12 2v7l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    return null;
  };

  // Notification button content
  const getNotificationButtonContent = () => {
    if (notificationState === 'processing') return loadingSpinner;
    
    if (notificationState === 'subscribe') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    return null;
  };

  // Don't render anything if not visible or both buttons are hidden
  if (!visible || (pwaState === 'hidden' && notificationState === 'hidden')) {
    return null;
  }

  return (
    <>
      {/* PWA Install/Open Button */}
      {pwaState !== 'hidden' && (
        <PWAButton
          color="white"
          onClick={pwaState === 'install' ? handlePWAInstall : handleOpenPWA}
          aria-label={pwaState === 'install' ? 'Cài đặt ứng dụng PWA' : 'Mở ứng dụng'}
          title={pwaState === 'install' ? 'Cài đặt ứng dụng PWA' : 'Mở ứng dụng'}
          disabled={isProcessing}
        >
          {getPWAButtonContent()}
        </PWAButton>
      )}

      {/* Notification Subscribe Button */}
      {notificationState !== 'hidden' && (
        <NotificationButton
          color="white"
          onClick={handleNotificationSubscribe}
          aria-label="Đăng ký nhận thông báo"
          title="Đăng ký nhận thông báo"
          disabled={isProcessing}
        >
          {getNotificationButtonContent()}
        </NotificationButton>
      )}

      {/* Toast Notifications */}
      <ToastNotification
        visible={toast.visible}
        type={toast.type}
      >
        {toast.message}
      </ToastNotification>
    </>
  );
};

export default SimpleNotificationButton; 